import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { isArray } from "lodash";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { BsChevronDown, BsChevronRight, BsFolder2, BsGripVertical, BsPlusLg } from "react-icons/bs";
import { LoginContext } from "helpers/LoginContext";
import { apiGetRequest } from "helpers/Requests";
import TableHeader from "components/common/Table/TableHeader";
import CustomButton from "components/common/Button/CustomButton";
import SelectOption from "components/common/Select/Select";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { capitalizeWords, classNames, parseBooleanVal } from "Utils/utils";
import SideBarNew from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import {
  SETTINGS_BREADCRUMB_PAGES,
  SP_SETTING_BREADCRUMB,
} from "Constants/BreadcrumbConstant";
import { spStaffComplianceSettingsRequest } from "store/actions/sp.actions";
import { TOAST_TYPE } from "Constants/constant";
import {
  AddDocumentModal,
  ComplianceSummaryBar,
  CreateCategoryModal,
  DocumentCategoryCard,
  EditDocumentModal,
  EmailCard,
  FormsSection,
  InstructionCard,
} from "components/StaffCompliance";
import SweetAlertConfirmationModal from "components/common/SweetAlertConfirmation/SweetAlertConfirmation";

const STAFF_COMPLIANCE_SELECTED_ROLE_KEY = "sp_staff_compliance_selected_role_id";

const readStoredComplianceRoleId = () => {
  try {
    const stored = localStorage.getItem(STAFF_COMPLIANCE_SELECTED_ROLE_KEY);
    if (!stored) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const findDefaultComplianceRole = (rolesList) => {
  if (!Array.isArray(rolesList) || !rolesList.length) return null;
  return rolesList.find((role) => role?.is_default_compliance === true) ?? null;
};

const StaffCompliance = ({
  spStaffComplianceSetting,
  spStaffComplianceLoader,
  spStaffComplianceSettingsUpdateRequest,
  spStaffComplianceUpdateLoader,
  spStaffComplianceAddDocumentRequest,
  spStaffComplianceCreateCategoryRequest,
  spStaffComplianceCategoryVisibilityRequest,
  spStaffComplianceCategoryUpdateRequest,
  spStaffComplianceCategoryDeleteRequest,
  spStaffComplianceDeleteDocumentRequest,
  spStaffComplianceAddDocumentCompleted,
  spStaffComplianceCreateCategoryCompleted,
  spStaffComplianceAddDocumentClear,
  spStaffComplianceCreateCategoryClear,
}) => {
  const dispatch = useDispatch();
  const { isUserGracePeriodOver, sideBarPadding } = useContext(LoginContext);

  // Expand/collapse is handled per category inside DocumentCategoryCard (isExpanded state)
  const [lastErrorLocation, setLastErrorLocation] = useState([]);

  const [compliancesData, setCompliancesData] = useState({
    document: [],
    form: [],
  });

  const [workerComplianceEmailNotification, setWorkerComplianceEmailNotification] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [documentsExpanded, setDocumentsExpanded] = useState(true);
  const [formsExpanded, setFormsExpanded] = useState(true);
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [createCategoryModalOpen, setCreateCategoryModalOpen] = useState(false);
  const [addDocumentPreselectedCategoryId, setAddDocumentPreselectedCategoryId] = useState(null);
  const [isAddDocumentSubmitting, setIsAddDocumentSubmitting] = useState(false);
  const [isCreateCategorySubmitting, setIsCreateCategorySubmitting] = useState(false);
  const [visibilityUpdatingId, setVisibilityUpdatingId] = useState(null);
  // TODO(STAFF_COMPLIANCE_DELETE): FE-6/A10,A11 – Delete document: confirm modal + loading. Cleared when spStaffComplianceSetting updates.
  const [deleteDocumentConfirmItem, setDeleteDocumentConfirmItem] = useState(null);
  const [deleteDocumentLoadingId, setDeleteDocumentLoadingId] = useState(null);
  const [deleteCategoryConfirmItem, setDeleteCategoryConfirmItem] = useState(null);
  const [categoryUpdateLoadingId, setCategoryUpdateLoadingId] = useState(null);
  const [categoryDeleteLoadingId, setCategoryDeleteLoadingId] = useState(null);
  const [editDocumentItem, setEditDocumentItem] = useState(null);
  const addDocumentSubmittingRef = useRef(false);
  const createCategorySubmittingRef = useRef(false);
  const visibilityToggleInFlightRef = useRef(new Set());
  const editSaveInProgressRef = useRef(false);

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(readStoredComplianceRoleId);
  const [rolesLoading, setRolesLoading] = useState(false);
  const defaultComplianceRole = findDefaultComplianceRole(roles);

  const roleOptions = useMemo(
    () =>
      (Array.isArray(roles) ? roles : [])
        .filter((r) => r != null && r.id != null)
        .map((r) => ({ label: r.name, value: r.id })),
    [roles]
  );
  const selectedRoleOption =
    selectedRole != null
      ? roleOptions.find((r) => String(r.value) === String(selectedRole)) ??
        null
      : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    setCompliancesData({ document: [], form: [] });
    dispatch(spStaffComplianceSettingsRequest(selectedRole));
  }, [selectedRole, dispatch]);

  useEffect(() => {
    setRolesLoading(true);
    apiGetRequest("/sp/roles-and-permission/roles?type=black_portal")
      .then((res) => {
        if (res.status === 200) {
          setRoles(Array.isArray(res.data?.data) ? res.data.data : []);
        } else {
          setRoles([]);
        }
      })
      .catch(() => {
        setRoles([]);
      })
      .finally(() => {
        setRolesLoading(false);
      });
  }, []);

  useEffect(() => {
    try {
      if (selectedRole) {
        localStorage.setItem(STAFF_COMPLIANCE_SELECTED_ROLE_KEY, String(selectedRole));
      } else {
        localStorage.removeItem(STAFF_COMPLIANCE_SELECTED_ROLE_KEY);
      }
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }, [selectedRole]);

  useEffect(() => {
    if (!roles.length) return;

    if (selectedRole != null) {
      const roleStillExists = roles.some(
        (r) => String(r?.id) === String(selectedRole)
      );
      if (!roleStillExists) {
        setSelectedRole(null);
        try {
          localStorage.removeItem(STAFF_COMPLIANCE_SELECTED_ROLE_KEY);
        } catch {
          // ignore
        }
      }
      return;
    }

    const defaultRole = findDefaultComplianceRole(roles);
    if (defaultRole?.id) {
      setSelectedRole(defaultRole.id);
    }
  }, [roles, selectedRole]);

  useEffect(() => {
    if (spStaffComplianceSetting) {
      setCompliancesData({
        document: spStaffComplianceSetting.document || [],
        form: spStaffComplianceSetting.form || [],
      });
      setWorkerComplianceEmailNotification(
        spStaffComplianceSetting.worker_compliance_email_notification || false
      );
      setVisibilityUpdatingId(null);
      setDeleteDocumentLoadingId(null);
      setCategoryUpdateLoadingId(null);
      setCategoryDeleteLoadingId(null);
      visibilityToggleInFlightRef.current.clear();
    }
  }, [spStaffComplianceSetting]);

  // Close Edit modal only after update API completes (loader goes false)
  useEffect(() => {
    if (!spStaffComplianceUpdateLoader && editSaveInProgressRef.current) {
      editSaveInProgressRef.current = false;
      setEditDocumentItem(null);
    }
  }, [spStaffComplianceUpdateLoader]);

  useEffect(() => {
    if (spStaffComplianceAddDocumentCompleted == null) return;
    addDocumentSubmittingRef.current = false;
    setIsAddDocumentSubmitting(false);
    if (spStaffComplianceAddDocumentCompleted?.success) {
      setAddDocumentModalOpen(false);
    }
    dispatch(spStaffComplianceAddDocumentClear());
  }, [spStaffComplianceAddDocumentCompleted, dispatch, spStaffComplianceAddDocumentClear]);

  useEffect(() => {
    if (spStaffComplianceCreateCategoryCompleted == null) return;
    createCategorySubmittingRef.current = false;
    setIsCreateCategorySubmitting(false);
    if (spStaffComplianceCreateCategoryCompleted?.success) {
      setCreateCategoryModalOpen(false);
    }
    dispatch(spStaffComplianceCreateCategoryClear());
  }, [spStaffComplianceCreateCategoryCompleted, dispatch, spStaffComplianceCreateCategoryClear]);

  const handleDiscard = () => {
    if (spStaffComplianceSetting) {
      setCompliancesData({
        document: spStaffComplianceSetting.document || [],
        form: spStaffComplianceSetting.form || [],
      });
      setWorkerComplianceEmailNotification(
        spStaffComplianceSetting.worker_compliance_email_notification || false
      );
      setLastErrorLocation([]);
    }
  };

  const handleSubmit = () => {
    if (isUserGracePeriodOver || lastErrorLocation.length > 0) {
      return;
    }
    spStaffComplianceSettingsUpdateRequest({
      worker_compliance_settings: [
        ...(compliancesData?.document || []),
        ...(compliancesData?.form || []),
      ],
      worker_compliance_email_notification: workerComplianceEmailNotification,
      role_id: selectedRole || undefined,
    });
  };

  const handleSwitchChange = (complianceType, item, value, type = "score") => {
    const prevSwitches = _.cloneDeep(compliancesData);
    if (prevSwitches?.[`${complianceType}`]) {
      if (!isArray(prevSwitches?.[`${complianceType}`])) {
        Object.assign(prevSwitches?.[`${complianceType}`], []);
      }
      const index = prevSwitches[`${complianceType}`]?.findIndex(
        (x) => x.id === item.id
      );
      if (index !== -1) {
        if (type === "score") {
          prevSwitches[`${complianceType}`][index].score = Number(value);
        } else if (type === "require_issue_date") {
          prevSwitches[`${complianceType}`][index].require_issue_date = !!value;
        } else if (type === "require_expiry_date") {
          prevSwitches[`${complianceType}`][index].require_expiry_date = !!value;
        } else {
          prevSwitches[`${complianceType}`][index].is_compliance = value;
        }
      } else {
        console.log("in else");
        // if (type === "score") {
        //   prevSwitches[`${complianceType}`]?.push({
        //     id: item.id,
        //     score: value,
        //     name: item.name,
        //   });
        // } else {
        //   prevSwitches[`${complianceType}`]?.push({
        //     id: item.id,
        //     is_compliance: value,
        //     name: item.name,
        //   });
        // }
      }
    }
    // else {
    //   if (type === "score") {
    //     Object.assign(prevSwitches, {
    //       [`${complianceType}`]: [
    //         { id: item.id, score: Number(value), name: item.name },
    //       ],
    //     });
    //   } else {
    //     Object.assign(prevSwitches, {
    //       [`${complianceType}`]: [
    //         { id: item.id, is_compliance: value, name: item.name },
    //       ],
    //     });
    //   }
    // }

    setCompliancesData({ ...prevSwitches });
  };

  const calculateTotalPercentage = () => {
    const documentScore = compliancesData?.document
      .filter((doc) => doc.is_compliance)
      .reduce((acc, doc) => acc + doc.score, 0);

    const formScore = compliancesData?.form
      .filter((form) => form.is_compliance)
      .reduce((acc, form) => acc + form.score, 0);

    const totalScore = documentScore + formScore;

    if (lastErrorLocation.length > 0 && Number(totalScore) === 100) {
      setLastErrorLocation([]);
    }
    return totalScore;
  };

  const checkIsAnyComplianceTrueWithZeroScore = () => {
    const data = [...compliancesData.document, ...compliancesData.form];
    return data.some(
      (x) => x.is_compliance === true && Number(x.score) <= 0
    );
  };

  const isSubmitDisabled =
    checkIsAnyComplianceTrueWithZeroScore() ||
    lastErrorLocation.length > 0 ||
    spStaffComplianceLoader ||
    spStaffComplianceUpdateLoader ||
    isUserGracePeriodOver ||
    Number(calculateTotalPercentage()) !== 100;

  const categories = spStaffComplianceSetting?.categories || [];

  const displayCategories = useMemo(() => {
    const docs = compliancesData.document || [];
    const merged = [...categories];
    const knownNames = new Set(categories.map((c) => c.name));
    const knownIds = new Set(
      categories.map((c) => c.id).filter((id) => id != null)
    );

    docs.forEach((d) => {
      const name = d.category ?? d.category_name ?? "undefined";
      const id = d.worker_compliance_category_id;
      const knownByName = knownNames.has(name);
      const knownById = id != null && knownIds.has(Number(id));
      if (!knownByName && !knownById) {
        merged.push({
          id: id ?? null,
          name,
          uuid: null,
          is_visible: true,
          sp_id: null,
        });
        knownNames.add(name);
        if (id != null) {
          knownIds.add(Number(id));
        }
      }
    });

    return merged;
  }, [categories, compliancesData.document]);

  const getDocumentsForCategory = (categoryMeta) => {
    const categoryName = categoryMeta?.name ?? "undefined";
    const categoryId = categoryMeta?.id;

    return (compliancesData.document || []).filter((item) => {
      const itemCategoryId = item.worker_compliance_category_id;
      if (
        categoryId != null &&
        itemCategoryId != null &&
        Number(itemCategoryId) === Number(categoryId)
      ) {
        return true;
      }

      return (item.category ?? item.category_name ?? "undefined") === categoryName;
    });
  };

  const handleDocumentMove = (itemId, fromCategory, toCategory, insertIndex) => {
    const toCategoryMeta = categories.find((c) => c.name === toCategory);
    const worker_compliance_category_id = toCategoryMeta?.id ?? null;
    const category_name = toCategory !== "undefined" ? toCategory : null;

    const doc = compliancesData.document || [];
    const item = doc.find((d) => d.id === itemId);
    if (!item) return;

    const cat = (d) => d.category ?? "undefined";
    const rest = doc.filter((d) => d.id !== itemId);
    const updated = {
      ...item,
      category: toCategory,
      worker_compliance_category_id: worker_compliance_category_id ?? item.worker_compliance_category_id,
      category_name: category_name ?? item.category_name,
    };

    const toItems = rest.filter((d) => cat(d) === toCategory);
    const idx =
      insertIndex != null
        ? Math.min(insertIndex, toItems.length)
        : toItems.length;

    const newToItems = [...toItems];
    newToItems.splice(idx, 0, updated);

    const cats = [...new Set([...doc.map(cat), toCategory])];
    const newDocuments = [];
    for (const c of cats) {
      if (c === toCategory) {
        newDocuments.push(...newToItems);
      } else if (c === fromCategory) {
        newDocuments.push(...rest.filter((d) => cat(d) === fromCategory));
      } else {
        newDocuments.push(...rest.filter((d) => cat(d) === c));
      }
    }

    setCompliancesData((prev) => ({ ...prev, document: newDocuments }));

    // Persist only the moved row (cross-category or reorder) to avoid mass role copy-on-write.
    const changedDocuments = [{ ...updated, insert_index: idx }];

    spStaffComplianceSettingsUpdateRequest({
      worker_compliance_settings: [...changedDocuments, ...(compliancesData.form || [])],
      worker_compliance_email_notification: workerComplianceEmailNotification,
      role_id: selectedRole || undefined,
    });
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragOver = (event) => {
    setOverId(event.over?.id ?? null);
  };

  const handleDragEnd = (event) => {
    setActiveDragId(null);
    setOverId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const data = active.data.current;
    if (!data?.item) return;

    const itemId = data.item.id;
    const fromCategory = data.fromCategory ?? "undefined";

    const overStr = String(over.id);
    const docs = compliancesData.document || [];

    if (overStr.startsWith("category-")) {
      const toCategory = overStr.replace(/^category-/, "");
      handleDocumentMove(itemId, fromCategory, toCategory);
    } else {
      const overDoc = docs.find((d) => d.id === over.id);
      if (!overDoc) return;

      const toCategory = overDoc.category ?? "undefined";
      const toItems = docs.filter(
        (d) => (d.category ?? "undefined") === toCategory
      );
      const insertIndex = toItems.findIndex((d) => d.id === over.id);
      handleDocumentMove(itemId, fromCategory, toCategory, insertIndex);
    }
  };

  const handleAddDocument = (preselectedCategoryName = null) => {
    const cat = preselectedCategoryName
      ? categories.find((c) => c.name === preselectedCategoryName)
      : null;
    setAddDocumentPreselectedCategoryId(cat?.id ?? null);
    setAddDocumentModalOpen(true);
  };

  const handleCreateCategory = () => {
    setCreateCategoryModalOpen(true);
  };

  const handleAddDocumentSubmit = (payload) => {
    if (addDocumentSubmittingRef.current) return;
    addDocumentSubmittingRef.current = true;
    setIsAddDocumentSubmitting(true);
    dispatch(
      spStaffComplianceAddDocumentRequest({
        ...payload,
        role_id: selectedRole || undefined,
      })
    );
  };

  const handleCreateCategorySubmit = (payload) => {
    if (createCategorySubmittingRef.current) return;
    createCategorySubmittingRef.current = true;
    setIsCreateCategorySubmitting(true);
    dispatch(
      spStaffComplianceCreateCategoryRequest({
        ...payload,
        role_id: selectedRole || undefined,
      })
    );
  };

  const handleCategoryVisibilityToggle = (categoryId, isVisible) => {
    if (visibilityToggleInFlightRef.current.has(categoryId)) return;
    visibilityToggleInFlightRef.current.add(categoryId);
    setVisibilityUpdatingId(categoryId);
    dispatch(
      spStaffComplianceCategoryVisibilityRequest(categoryId, isVisible, selectedRole)
    );
  };

  const handleCategoryUpdate = (categoryId, name) => {
    setCategoryUpdateLoadingId(categoryId);
    dispatch(spStaffComplianceCategoryUpdateRequest(categoryId, name, selectedRole));
  };

  const handleCategoryDeleteClick = (categoryId, categoryName) => {
    setDeleteCategoryConfirmItem({ uuid: categoryId, name: categoryName });
  };
  const handleCategoryDeleteConfirm = (confirmed) => {
    if (confirmed && deleteCategoryConfirmItem?.uuid) {
      setCategoryDeleteLoadingId(deleteCategoryConfirmItem.uuid);
      dispatch(
        spStaffComplianceCategoryDeleteRequest(deleteCategoryConfirmItem.uuid, selectedRole)
      );
    }
    setDeleteCategoryConfirmItem(null);
  };

  const handleDeleteDocumentClick = (item) => {
    if (!item?.uuid) return;
    setDeleteDocumentConfirmItem({ uuid: item.uuid, name: item.name });
  };
  const handleDeleteDocumentConfirm = (confirmed) => {
    if (confirmed && deleteDocumentConfirmItem?.uuid) {
      const uuid = deleteDocumentConfirmItem.uuid;
      setDeleteDocumentLoadingId(uuid);
      dispatch(spStaffComplianceDeleteDocumentRequest(uuid, selectedRole));
    }
    setDeleteDocumentConfirmItem(null);
  };

  const handleEditDocumentClick = (item) => {
    setEditDocumentItem(item);
  };

  const handleEditDocumentSave = (name, score, isCompliance, workerComplianceCategoryId) => {
    if (!editDocumentItem) return;
    const selectedCategory = categories.find(
      (c) => c.id === workerComplianceCategoryId || c.id === Number(workerComplianceCategoryId)
    );
    const categoryName = selectedCategory?.name ?? editDocumentItem.category ?? editDocumentItem.category_name;
    const newDocuments = (compliancesData.document || []).map((d) =>
      d.id === editDocumentItem.id
        ? {
            ...d,
            name: name || d.name,
            score,
            is_compliance: isCompliance,
            worker_compliance_category_id: workerComplianceCategoryId ?? d.worker_compliance_category_id,
            category: categoryName ?? d.category,
            category_name: categoryName ?? d.category_name,
          }
        : d
    );
    setCompliancesData((prev) => ({ ...prev, document: newDocuments }));
    editSaveInProgressRef.current = true;
    // Modal stays open with loader until API completes (cleared in useEffect when spStaffComplianceUpdateLoader goes false)
    spStaffComplianceSettingsUpdateRequest({
      worker_compliance_settings: [...newDocuments, ...(compliancesData.form || [])],
      worker_compliance_email_notification: workerComplianceEmailNotification,
      role_id: selectedRole || undefined,
    });
  };

  return (
    <div>
      <div className="min-h-screen bg-gray-100">
        <SideBarNew />
        <div
        className={`${
            sideBarPadding
              ? "lg:pl-[4rem] duration-300"
              : "lg:pl-64 duration-300"
          }  flex flex-col`}
        >
          <Header
            title="Worker Compliance Settings"
            breadcrumb={
              <Breadcrumb
                pages={SP_SETTING_BREADCRUMB(
                  SETTINGS_BREADCRUMB_PAGES.WORKER_COMPLIANCE_SETTING
                )}
              />
            }
            btnComponentBeforeRegions={
              <CustomButton
                type="button"
                variant="primary"
                clickHandler={handleAddDocument}
                label="Add New Document"
                Icon={<BsPlusLg className="w-4 h-4" />}
              />
            }
          />
          <section className="container mx-auto sm:px-6 lg:px-8 mt-3 pb-10 mb-6">
            {spStaffComplianceLoader ? (
              <div className={`flex flex-col w-full min-h-screen bg-gray-50 items-center justify-center`}>
                <CircularLoader
                  classes="flex justify-center items-center"
                  classloader="loader-l"
                />
              </div>
            ) : (
              <div className="mt-5 md:mt-0 md:col-span-2">
                <div className="max-w-7xl mx-auto mb-4">
                  <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-100 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-gray-200 bg-gray-50/40 p-4 flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Compliance Role
                          </label>
                          <SelectOption
                            data={roleOptions}
                            selectedOption={selectedRoleOption}
                            handleChange={(option) =>
                              setSelectedRole(
                                option?.value != null ? option.value : null
                              )
                            }
                            placeHolder="Select a role"
                            isSearch={false}
                            isClearable={true}
                            loading={rolesLoading}
                            isApiLoading={rolesLoading}
                            isDisabled={rolesLoading}
                            name="compliance_role"
                          />
                          <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                            Configure documents per role.
                            {/* {defaultComplianceRole?.name
                              ? ` ${capitalizeWords(defaultComplianceRole.name)} is selected by default.`
                              : ""} */}
                          </p>
                        </div>

                        <EmailCard
                          checked={workerComplianceEmailNotification}
                          onChange={setWorkerComplianceEmailNotification}
                        />
                      </div>

                      <InstructionCard
                        totalPercentage={calculateTotalPercentage()}
                        isValid={!checkIsAnyComplianceTrueWithZeroScore()}
                      />
                    </div>

                    <div className="px-6 py-6">
                      <div className="mb-5">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Staff Onboarding
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Mandatory documents and forms for worker onboarding.
                        </p>
                      </div>

                      <div className="space-y-4">
                      <div className="w-full rounded-lg border border-gray-200 overflow-hidden">
                    <div
                      className="flex items-center justify-between px-5 py-4 bg-gray-50/80 border-b border-gray-200 cursor-pointer select-none w-full"
                      onClick={() => setDocumentsExpanded((prev) => !prev)}
                    >
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocumentsExpanded((prev) => !prev);
                        }}
                        className="flex-shrink-0 p-0.5 text-gray-500 hover:text-indigo-600 rounded"
                      >
                        {documentsExpanded ? (
                          <BsChevronDown className="w-4 h-4" />
                        ) : (
                          <BsChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      <BsFolder2 className="w-5 h-5 text-indigo-600" />
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                        Documents
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateCategory();
                      }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-white border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                    >
                      <BsPlusLg className="w-4 h-4" />
                      New Category
                    </button>
                    </div>
                    {documentsExpanded && (
                    <div className="p-4 bg-white border-t-0 relative">
                      {spStaffComplianceUpdateLoader && (
                        <div
                          className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/80 rounded-lg"
                          aria-live="polite"
                          aria-busy="true"
                        >
                          <CircularLoader
                            classes="flex justify-center items-center"
                            classloader="loader-m"
                          />
                          <p className="mt-3 text-sm font-medium text-gray-700">
                            Saving…
                          </p>
                        </div>
                      )}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="space-y-4">
                      {displayCategories.map((categoryMeta) => {
                          const categoryName = categoryMeta?.name ?? "undefined";
                          const items = getDocumentsForCategory(categoryMeta);
                          const hasMandatoryDoc = items.some(
                            (i) => i.is_compliance === true
                          );
                          return (
                            <DocumentCategoryCard
                              key={categoryName}
                              categoryName={categoryName}
                              items={items}
                              categoryId={categoryMeta?.uuid}
                              isVisible={
                                categoryMeta?.is_visible !== false
                              }
                              hasMandatoryDoc={hasMandatoryDoc}
                              onVisibilityToggle={handleCategoryVisibilityToggle}
                              onCategoryUpdate={handleCategoryUpdate}
                              onCategoryDelete={handleCategoryDeleteClick}
                              categoryUpdateLoading={categoryUpdateLoadingId === categoryMeta?.uuid}
                              categoryDeleteLoading={categoryDeleteLoadingId === categoryMeta?.uuid}
                              isSystemDefault={categoryMeta?.sp_id == null}
                              visibilityLoading={visibilityUpdatingId === categoryMeta?.uuid}
                              complianceType="document"
                              showDateRequirements
                              lastErrorLocation={lastErrorLocation}
                              onSwitchChange={(item, value, type) =>
                                handleSwitchChange(
                                  "document",
                                  item,
                                  value,
                                  type
                                )
                              }
                              onScoreChange={(item, value) =>
                                handleSwitchChange(
                                  "document",
                                  item,
                                  value,
                                  "score"
                                )
                              }
                              onLastErrorChange={setLastErrorLocation}
                              onAddDocument={handleAddDocument}
                              onDeleteDocument={handleDeleteDocumentClick}
                              onEditDocument={handleEditDocumentClick}
                              deleteDocumentLoadingId={deleteDocumentLoadingId}
                              activeDragId={activeDragId}
                              activeDragFromCategory={
                                activeDragId
                                  ? compliancesData.document?.find(
                                      (d) => d.id === activeDragId
                                    )?.category ?? "undefined"
                                  : null
                              }
                              overId={overId}
                            />
                          );
                        }
                      )}
                    </div>
                    <DragOverlay dropAnimation={null}>
                      {activeDragId ? (
                        <div className="flex items-center gap-4 py-3 px-4 text-sm bg-white rounded-lg shadow-lg border-2 border-indigo-300 opacity-95 cursor-grabbing">
                          <div className="flex-shrink-0 text-indigo-500">
                            <BsGripVertical className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {(() => {
                                const docs = compliancesData.document || [];
                                const item = docs.find((d) => d.id === activeDragId);
                                return item?.name || "Document";
                              })()}
                            </p>
                            <p className="text-xs text-gray-500">Drop into a category</p>
                          </div>
                        </div>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                    </div>
                    )}
                  </div>

                      <div className="w-full rounded-lg border border-gray-200 overflow-hidden">
                    <div
                      className="flex items-center gap-2 px-5 py-4 bg-gray-50/80 border-b border-gray-200 cursor-pointer select-none w-full"
                    onClick={() => setFormsExpanded((prev) => !prev)}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormsExpanded((prev) => !prev);
                      }}
                      className="flex-shrink-0 p-0.5 text-gray-500 hover:text-indigo-600 rounded"
                    >
                      {formsExpanded ? (
                        <BsChevronDown className="w-4 h-4" />
                      ) : (
                        <BsChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                      Forms
                    </h4>
                    </div>
                    {formsExpanded && (
                    <div className="bg-white">
                    <FormsSection
                      embedded
                      forms={compliancesData.form || []}
                      lastErrorLocation={lastErrorLocation}
                      onSwitchChange={(item, value, type) =>
                        handleSwitchChange("form", item, value, type)
                      }
                      onScoreChange={(item, value) =>
                        handleSwitchChange("form", item, value, "score")
                      }
                      onLastErrorChange={setLastErrorLocation}
                    />
                    </div>
                    )}
                      </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-6 border-t border-gray-100 bg-gray-50/50">
                      <ComplianceSummaryBar
                        totalPercentage={calculateTotalPercentage()}
                        isValid={!checkIsAnyComplianceTrueWithZeroScore()}
                      />
                      <div className="flex justify-end gap-2 shrink-0">
                        <CustomButton
                          showLoading={spStaffComplianceUpdateLoader}
                          isDisabled={isSubmitDisabled}
                          variant="primary"
                          clickHandler={handleSubmit}
                          label="Update Changes"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <AddDocumentModal
        isOpen={addDocumentModalOpen}
        onClose={() => setAddDocumentModalOpen(false)}
        onSubmit={handleAddDocumentSubmit}
        categories={categories}
        preselectedCategoryId={addDocumentPreselectedCategoryId}
        isLoading={isAddDocumentSubmitting}
      />
      <CreateCategoryModal
        isOpen={createCategoryModalOpen}
        onClose={() => setCreateCategoryModalOpen(false)}
        onSubmit={handleCreateCategorySubmit}
        isLoading={isCreateCategorySubmitting}
      />
      <EditDocumentModal
        isOpen={!!editDocumentItem}
        onClose={() => setEditDocumentItem(null)}
        document={editDocumentItem}
        categories={categories}
        onSave={handleEditDocumentSave}
        isLoading={spStaffComplianceUpdateLoader}
      />
      <SweetAlertConfirmationModal
        isOpen={!!deleteDocumentConfirmItem}
        title="Delete document?"
        content={
          deleteDocumentConfirmItem
            ? `Are you sure you want to delete "${deleteDocumentConfirmItem.name}"? This action cannot be undone.`
            : ""
        }
        setIsConfirm={handleDeleteDocumentConfirm}
        dangerMode={true}
        onlyOkBtn={["No, Cancel", "Yes, Delete"]}
      />
      <SweetAlertConfirmationModal
        isOpen={!!deleteCategoryConfirmItem}
        title="Delete category?"
        content={
          deleteCategoryConfirmItem
            ? `Are you sure you want to delete "${deleteCategoryConfirmItem.name}"? All documents in this category will also be removed. This action cannot be undone.`
            : ""
        }
        setIsConfirm={handleCategoryDeleteConfirm}
        dangerMode={true}
        onlyOkBtn={["No, Cancel", "Yes, Delete"]}
      />
    </div>
  );
};

export default StaffCompliance;
